import { AES_IV_LENGTH, AES_TAG_LENGTH, HKDF_INFO } from '@/utils/constants/encryption';
import type { JwkPublicKey } from '@/types/crypto';

const encoder = new TextEncoder();
const decoder = new TextDecoder('utf-8');

/**
 * 生成 ECDH P-256 临时密钥对
 */
export async function generateEcdhKeyPair(): Promise<CryptoKeyPair> {
    return crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits', 'deriveKey']);
}

/**
 * 从服务端返回的 JWK 导入 ECDH 公钥
 */
export async function importServerPublicKey(jwk: JwkPublicKey): Promise<CryptoKey> {
    return crypto.subtle.importKey('jwk', jwk, { name: 'ECDH', namedCurve: 'P-256' }, false, []);
}

/**
 * 导出客户端公钥为 JWK 对象
 */
export async function exportClientPublicKeyJwk(keyPair: CryptoKeyPair): Promise<JwkPublicKey> {
    const jwk = await crypto.subtle.exportKey('jwk', keyPair.publicKey);
    return {
        kty: 'EC',
        crv: 'P-256',
        x: jwk.x as string,
        y: jwk.y as string
    };
}

/**
 * 使用 HKDF-SHA256 从 ECDH 共享密钥派生 AES-256-GCM 会话密钥
 */
export async function deriveSessionKey(privateKey: CryptoKey, publicKey: CryptoKey): Promise<CryptoKey> {
    // 1. ECDH 共享密钥
    const sharedSecret = await crypto.subtle.deriveBits({ name: 'ECDH', public: publicKey }, privateKey, 256);

    // 2. 将共享密钥导入为 HKDF 基础密钥
    const hkdfBase = await crypto.subtle.importKey('raw', sharedSecret, { name: 'HKDF' }, false, ['deriveKey']);

    // 3. 使用 HKDF-SHA256 派生 AES-256-GCM 密钥；salt 为 32 字节全 0，与后端保持一致
    const salt = new Uint8Array(32);
    return crypto.subtle.deriveKey(
        { name: 'HKDF', hash: 'SHA-256', salt, info: encoder.encode(HKDF_INFO) },
        hkdfBase,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
    );
}

/**
 * AES-256-GCM 加密
 */
export async function encryptAesGcm(
    key: CryptoKey,
    iv: ArrayBuffer,
    plaintext: ArrayBuffer | string
): Promise<ArrayBuffer> {
    const data = typeof plaintext === 'string' ? encoder.encode(plaintext) : plaintext;
    return crypto.subtle.encrypt({ name: 'AES-GCM', iv, tagLength: AES_TAG_LENGTH }, key, data);
}

/**
 * AES-256-GCM 解密
 */
export async function decryptAesGcm(key: CryptoKey, iv: ArrayBuffer, ciphertext: ArrayBuffer): Promise<ArrayBuffer> {
    return crypto.subtle.decrypt({ name: 'AES-GCM', iv, tagLength: AES_TAG_LENGTH }, key, ciphertext);
}

/**
 * 生成随机 IV
 */
export function generateIv(): ArrayBuffer {
    return crypto.getRandomValues(new Uint8Array(AES_IV_LENGTH)).buffer;
}

/**
 * 生成随机 nonce（Base64）
 */
export function generateNonce(): string {
    const bytes = crypto.getRandomValues(new Uint8Array(12));
    return arrayBufferToBase64(bytes.buffer);
}

/**
 * ArrayBuffer 转 Base64
 */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

/**
 * Base64 转 ArrayBuffer
 */
export function base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
}

/**
 * ArrayBuffer 转 UTF-8 字符串
 */
export function arrayBufferToString(buffer: ArrayBuffer): string {
    return decoder.decode(buffer);
}

/**
 * UTF-8 字符串转 ArrayBuffer
 */
export function stringToArrayBuffer(text: string): ArrayBuffer {
    return encoder.encode(text).buffer;
}

/**
 * 将 JSON 对象序列化为 UTF-8 字符串
 */
export function jsonToString(data: unknown): string {
    return JSON.stringify(data);
}

/**
 * 解析 JSON 字符串
 */
export function parseJson<T = unknown>(text: string): T {
    return JSON.parse(text) as T;
}
